import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CallProvider, useCall } from './context/CallContext';
import { prefetchIapProduct } from './utils/checkout';
import Timeline from './pages/Timeline';
import TalkRooms from './pages/TalkRooms';
import TalkPremium from './pages/TalkPremium';
import TalkVoiceRoom from './pages/TalkVoiceRoom';
import Chat from './pages/Chat';
import ChatRoom from './pages/ChatRoom';
import Profile from './pages/Profile';
import Search from './pages/Search';
import BottomNav from './components/BottomNav';
import { ThemeProvider } from './context/ThemeContext';
import Settings from './pages/Settings';
import ConsentScreen from './pages/ConsentScreen';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Support from './pages/Support';
import Terms from './pages/Terms';
import Notebooks from './pages/Notebooks';
import Login from './pages/Login';
import PrivateCall from './pages/PrivateCall';

const CONSENT_KEY = 'consent_agreed';
function hasConsent() { return localStorage.getItem(CONSENT_KEY) === 'true'; }

// 画面描画中の例外で「真っ白なページ」にならないためのエラーバウンダリ（2.1(a)対策）
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.errorWrap}>
          <p style={styles.errorTitle}>問題が発生しました</p>
          <p style={styles.errorDesc}>画面の表示中にエラーが発生しました。</p>
          <button data-control="primary" data-control-id="App-errorBtn"
            type="button"
            style={styles.errorBtn}
            onClick={() => { this.setState({ hasError: false }); window.location.href = '/'; }}
          >
            ひろばに戻る
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={styles.loading}>
      <div style={styles.spinnerWrap}>
        <div style={styles.spinner} />
        <p style={styles.loadingText}>読み込み中...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function FloatingCallBar() {
  const call = useCall();
  const location = useLocation();
  const navigate = useNavigate();

  const onCallPage = location.pathname.startsWith('/call/');
  if (!call.activeCall || call.callStatus === 'idle' || onCallPage) return null;

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <aside className="jt-active-call" aria-label="進行中の通話">
      <button className="jt-active-call-return" onClick={() => navigate(`/call/${call.activeCall.partnerId}`)}>
        <span>{call.activeCall.partnerName || '音声通話'}</span>
        <small>{call.callStatus === 'connected' ? fmt(call.duration) : call.callStatus === 'calling' ? '接続中' : '通話を確認'} — 通話画面へ</small>
      </button>
      <button className="jt-active-call-end" onClick={call.endCall}>通話を終了</button>
    </aside>
  );
}

const AppRoutes = () => {
  const { user } = useAuth();
  const location = useLocation();
  const conversationDetail = /^\/(chat|call)\/[^/]+/.test(location.pathname);
  const call = useCall();
  const hasCallBanner = call.activeCall && call.callStatus !== 'idle' && !location.pathname.startsWith('/call/');
  const [consentAgreed, setConsentAgreed] = useState(hasConsent);

  return (
    <div style={{ ...styles.app, '--call-banner-space': hasCallBanner ? 'calc(60px + env(safe-area-inset-top))' : '0px' }}>
      <FloatingCallBar />
      <div style={consentAgreed && user && !conversationDetail ? { ...styles.content, paddingTop: hasCallBanner ? 60 : 0 } : undefined}>
        <Routes>
          <Route path="/privacy" element={<PrivacyPolicy />} />
          {/* Guideline 1.5: サポート情報は登録・ログイン前からも到達できること */}
          <Route path="/support" element={<Support />} />
          {/* Guideline 1.2: 規約は登録・ログイン前（同意ゲートの外）から読めること */}
          <Route path="/terms" element={<Terms />} />
          {!consentAgreed ? (
            <Route path="*" element={<ConsentScreen onAgree={() => setConsentAgreed(true)} />} />
          ) : (
            <>
              <Route path="/login" element={<Login />} />
              <Route path="/"                element={<PrivateRoute><Timeline /></PrivateRoute>} />
              <Route path="/talk"            element={<PrivateRoute><TalkRooms /></PrivateRoute>} />
              <Route path="/talk/premium"    element={<PrivateRoute><TalkPremium /></PrivateRoute>} />
              <Route path="/talk/:roomId"    element={<PrivateRoute><TalkVoiceRoom /></PrivateRoute>} />
              <Route path="/chat"            element={<PrivateRoute><Chat /></PrivateRoute>} />
              <Route path="/chat/:partnerId" element={<PrivateRoute><ChatRoom /></PrivateRoute>} />
              <Route path="/call/:partnerId" element={<PrivateRoute><PrivateCall /></PrivateRoute>} />
              <Route path="/search"          element={<PrivateRoute><Search /></PrivateRoute>} />
              <Route path="/profile/:userId?" element={<PrivateRoute><Profile /></PrivateRoute>} />
              <Route path="/words/:notebookId?" element={<PrivateRoute><Notebooks /></PrivateRoute>} />
          <Route path="/settings"        element={<PrivateRoute><Settings /></PrivateRoute>} />
            </>
          )}
        </Routes>
      </div>
      {consentAgreed && user && !conversationDetail && <BottomNav />}
    </div>
  );
};

export default function App() {
  useEffect(() => { prefetchIapProduct(); }, []);
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <CallProvider>
              <AppRoutes />
            </CallProvider>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

const styles = {
  app: {
    maxWidth: 'var(--app-max-w)', margin: '0 auto', minHeight: '100dvh',
    background: 'var(--surface)', position: 'relative',
    boxShadow: 'none',
    paddingTop: 'env(safe-area-inset-top)',
  },
  content: { paddingBottom: 'calc(80px + env(safe-area-inset-bottom))' },
  loading: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--surface)' },
  spinnerWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 },
  spinner: { width: 44, height: 44, border: '4px solid var(--primary-light)', borderTop: '4px solid var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  loadingText: { fontSize: 13, color: 'var(--text-muted)', fontWeight: 700, letterSpacing: '0.04em' },
  floatingBar: {
    position: 'fixed', top: 'env(safe-area-inset-top)', left: '50%', transform: 'translateX(-50%)',
    width: '100%', maxWidth: 'var(--app-max-w)', zIndex: 500,
    background: '#22c55e', display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 16px', cursor: 'pointer',
  },
  floatingDot: { width: 8, height: 8, borderRadius: '50%', background: '#fff', flexShrink: 0, animation: 'pulse 1.5s ease infinite' },
  floatingName: { flex: 1, fontSize: 14, fontWeight: 700, color: '#fff' },
  floatingTime: { fontSize: 13, color: 'rgba(255,255,255,0.85)', fontVariantNumeric: 'tabular-nums' },
  floatingEnd: {
    background: 'rgba(0,0,0,0.2)', border: 'none', borderRadius: 20,
    padding: '4px 12px', fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer',
  },
  errorWrap: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: 24, background: '#0b0b14', textAlign: 'center',
  },
  errorTitle: { fontSize: 17, fontWeight: 800, color: '#f0ece0', margin: 0 },
  errorDesc: { fontSize: 13, color: 'rgba(240,236,224,0.6)', margin: 0 },
  errorBtn: {
    marginTop: 14, padding: '12px 28px', borderRadius: 14, border: 'none',
    background: '#34d399', color: '#04120c', fontSize: 15, fontWeight: 800,
    fontFamily: 'inherit', cursor: 'pointer',
  },
};
